import { useState, useEffect, useRef } from "react";
import * as d3 from "d3";
import { Axes } from "./Axes";
import { Tooltip } from "./Tooltip";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";

// Category color mapping
const CATEGORIES = {
  "Classic Literature": "#7C2233",
  "Modern Fiction": "#C26E5A",
  "Postmodern": "#2F6B6D",
  "Business": "#24456E",
  "Pop Science": "#C9A227",
  "Biography & Memoir": "#6E4066",
  "Philosophy & Society": "#5B6E3A"
};

// Prefer the revised category, fall back to the legacy label
const bookCategory = (d) => d.categoryV2 || d.category;

export const Scatterplot = () => {
  // Ref for the container
  const containerRef = useRef(null);

  // State
  const [containerSize, setContainerSize] = useState({ width: 800, height: 550 });
  const [interactionData, setInteractionData] = useState();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [bookData, setBookData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from Firebase
  useEffect(() => {
    const fetchBooks = async () => {
      setLoading(true)
      try {
        const booksCollection = collection(db, "books");
        const snapshot = await getDocs(booksCollection);
        const books = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBookData(books);
      } catch (error) {
        console.error("Error fetching books:", error);
      } finally {
        setLoading(false);

        setTimeout(() => {
          if (containerRef.current) {
            const { width, height } = containerRef.current.getBoundingClientRect();
            if (width > 0) {
              setContainerSize({ width, height });
            }
          }
        }, 50); // 50ms delay
      }
    };

    fetchBooks();
  }, []);

  if (loading) {
    return <div>Loading books data...</div>;
  }

  // Sort the data: bigger circles must appear at the bottom
  const sortedData = [...bookData].sort((a, b) => b.size - a.size);

  // Scales
  const margin = Math.min(containerSize.width, containerSize.height) * 0.05;
  const xScale = d3.scaleLinear().domain([0, 1]).range([margin, containerSize.width - margin]);
  const yScale = d3.scaleLinear().domain([0, 1]).range([containerSize.height - margin, margin]);
  const sizeScale = d3.scaleSqrt().domain([0, 2000]).range([3, 50]);

  // All circles, 1 per book
  const circles = sortedData.map((d, i) => {
    const size = sizeScale(d.size);
    const radius = size / 2;

    const xPos = xScale(d.x);
    const yPos = yScale(d.y);

    const category = bookCategory(d);
    const isFilteredOut = selectedCategory && category !== selectedCategory;
    const isDimmed =
      isFilteredOut ||
      (interactionData && bookCategory(interactionData) !== category);
    const className = isDimmed
      ?"scatterplotSquare dimmed" :"scatterplotSquare"

    return (
      <g
        key={i}
        style={isFilteredOut ? { pointerEvents: "none" } : undefined}
        onMouseMove={() =>
          setInteractionData({
            xPos,
            yPos,
            color: CATEGORIES[category] || "#999999",
            ...d,
          })
        }
        onMouseLeave={() => setInteractionData(undefined)}
      >
        <circle
          cx={xPos}
          cy={yPos}
          opacity={1}
          fill={CATEGORIES[category] || "#999999"}
          r={radius}
          className={className}
        />
      </g>
    );
  });

  // Category legend component; click a category to isolate it, click again to clear
  const CategoryLegend = () => (
    <div className="legend">
      <h3>Book Categories</h3>
      <div className="legendItems">
        {Object.entries(CATEGORIES).map(([category, color]) => {
          const isInactive = selectedCategory && selectedCategory !== category;
          return (
            <div
              key={category}
              className="legendItem"
              style={{ cursor: "pointer", opacity: isInactive ? 0.35 : 1 }}
              onClick={() =>
                setSelectedCategory(selectedCategory === category ? null : category)
              }
            >
              <div
                className="legendColor"
                style={{ backgroundColor: color }}
              ></div>
              <div className="legendLabel">{category}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Calculate bounding rect before return
const containerRect = containerRef.current
  ? containerRef.current.getBoundingClientRect()
  : { left: 0, top: 0 };

return (
  <div
    ref={containerRef}
    className="scatterplotContainer"
    style={{ position: "relative", width: "100%", height: "100%" }}
  >
    <CategoryLegend />
    <svg
      width={containerSize.width}
      height={containerSize.height}
      shapeRendering={"crispEdges"}
    >
      <g>
        <Axes
          x={xScale(0.43)}
          y={yScale(0.41)}
          width={containerSize.width}
          height={containerSize.height}
        />
        {circles}
      </g>
    </svg>

    {/* Tooltip overlay rendered on top */}
    <div
      style={{
        position: "fixed",
        left: containerRect.left,
        top: containerRect.top,
        width: containerSize.width,
        height: containerSize.height,
        pointerEvents: "none",
        zIndex: 2147483647,
      }}
    >
      <Tooltip interactionData={interactionData} />
    </div>
  </div>
  );
};